/**
 * AppContext.tsx
 * Drop into: src/AppContext.tsx
 *
 * Central data store. Any page that updates net worth, income, goals,
 * or balances should call the relevant refresh function so all pages
 * stay in sync without a full page reload.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api, isAbortError } from "./api";

interface NetWorthAccount {
  id: number;
  name: string;
  type: "asset" | "liability";
  group: string;
  balance: number;
}

interface Goal {
  id: number;
  name: string;
  type: "save" | "paydown";
  target: number;
  saved: number;
  monthly: number;
  color: string;
  emoji: string;
  targetDate?: string;
}

interface AppData {
  // Net worth
  accounts: NetWorthAccount[];
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;

  // Goals
  goals: Goal[];
  totalMonthlySavings: number;

  // Income (from financial snapshot)
  monthlyIncome: number;
  savingsRate: number;

  // Transactions — central cache. Every page that reads the current
  // user's full transaction list should consume from here so a
  // recategorize / split / exclude in one component immediately
  // propagates to every other component on the same screen.
  // Components viewing a different user (e.g. an advisor looking at a
  // client's transactions) still fetch directly — context is
  // per-active-user.
  transactions: any[];
  loadingTransactions: boolean;

  // Loading states
  loadingAccounts: boolean;
  loadingGoals: boolean;
}

interface AppContextValue extends AppData {
  refreshAccounts: () => Promise<void>;
  refreshGoals: () => Promise<void>;
  refreshSnapshot: () => Promise<void>;
  refreshTransactions: () => Promise<void>;
  userId: number | null;
  setUserId: (id: number) => void;
}

const defaultData: AppData = {
  accounts: [],
  totalAssets: 0,
  totalLiabilities: 0,
  netWorth: 0,
  goals: [],
  totalMonthlySavings: 0,
  monthlyIncome: 0,
  savingsRate: 0,
  transactions: [],
  loadingTransactions: false,
  loadingAccounts: false,
  loadingGoals: false,
};

const AppContext = createContext<AppContextValue>({
  ...defaultData,
  refreshAccounts: async () => {},
  refreshGoals: async () => {},
  refreshSnapshot: async () => {},
  refreshTransactions: async () => {},
  userId: null,
  setUserId: () => {},
});

/** `forUserId` pins the provider to one user. The advisor's phone preview
 *  wraps the client's MobileShell in a second provider pinned to that client;
 *  without it every mobile screen read the ADVISOR's transactions and goals
 *  (the outer provider follows the logged-in user), so a Top Categories bar
 *  computed server-side for the client drilled into an empty list. */
export function AppProvider({ children, forUserId }: { children: React.ReactNode; forUserId?: number }) {
  const [userId, setUserId] = useState<number | null>(forUserId ?? null);
  useEffect(() => { if (forUserId != null) setUserId(forUserId); }, [forUserId]);
  const [accounts, setAccounts] = useState<NetWorthAccount[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [savingsRate, setSavingsRate] = useState(0);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingGoals, setLoadingGoals] = useState(false);

  // Switching client is the one moment this store can lie. Four independent
  // reads are in flight at once, and the previous client's slower response
  // used to land after the new client's and win — the panel then showed the
  // wrong person's accounts with no spinner and no error to hint at it. Every
  // read is now tagged with the userId it was issued for and the controller
  // that can cancel it: a response for anyone but the CURRENT userId is
  // dropped, and the request itself is aborted so it usually never arrives.
  const abortersRef = useRef<Map<string, AbortController>>(new Map());
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  /** A signal for `key`, cancelling whatever previous read used that key. */
  const signalFor = useCallback((key: string): AbortSignal => {
    abortersRef.current.get(key)?.abort();
    const controller = new AbortController();
    abortersRef.current.set(key, controller);
    return controller.signal;
  }, []);

  /** True when `forUser` is still who the app is showing. */
  const stillCurrent = useCallback((forUser: number) => userIdRef.current === forUser, []);

  // Abort everything in flight on unmount or user switch.
  useEffect(() => () => {
    abortersRef.current.forEach((c) => c.abort());
    abortersRef.current.clear();
  }, [userId]);

  const refreshAccounts = useCallback(async () => {
    if (!userId) return;
    setLoadingAccounts(true);
    try {
      const data = await api.get(`/storage/networth/${userId}`, { signal: signalFor("accounts") });
      if (stillCurrent(userId)) setAccounts(data);
    } catch (e) {
      if (isAbortError(e)) return;  // superseded; leave the newer run's state alone
    } finally {
      if (stillCurrent(userId)) setLoadingAccounts(false);
    }
  }, [userId, signalFor, stillCurrent]);

  const refreshGoals = useCallback(async () => {
    if (!userId) return;
    setLoadingGoals(true);
    try {
      const data = await api.get(`/storage/goals/${userId}`, { signal: signalFor("goals") });
      if (stillCurrent(userId)) setGoals(data);
    } catch (e) {
      if (isAbortError(e)) return;
    } finally {
      if (stillCurrent(userId)) setLoadingGoals(false);
    }
  }, [userId, signalFor, stillCurrent]);

  const refreshSnapshot = useCallback(async () => {
    if (!userId) return;
    try {
      // Income = canonical CURRENT state (live-state derives it from the
      // IncomeSource rows), never a frozen FinancialSnapshot — otherwise
      // every consumer of monthlyIncome shows a stale figure.
      const live = await api.get(`/financial/live-state/${userId}`, { signal: signalFor("live") }).catch(() => null);
      if (live?.income_monthly != null && stillCurrent(userId)) setMonthlyIncome(live.income_monthly);
      // savings_rate is the advisor's planned TARGET (an input, not a
      // state number), which still lives on the snapshot until the
      // override layer gives it a canonical home. Best-effort, optional.
      const snap = await api.get(`/advisor/snapshot/${userId}/latest`, { signal: signalFor("snapshot") }).catch(() => null);
      if (snap?.savings_rate && stillCurrent(userId)) setSavingsRate(snap.savings_rate);
    } catch {}
  }, [userId, signalFor, stillCurrent]);

  const refreshTransactions = useCallback(async () => {
    if (!userId) return;
    setLoadingTransactions(true);
    try {
      const data = await api.get(`/transactions/user/${userId}`, { signal: signalFor("transactions") });
      if (stillCurrent(userId)) setTransactions(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isAbortError(e)) return;
    } finally {
      if (stillCurrent(userId)) setLoadingTransactions(false);
    }
  }, [userId, signalFor, stillCurrent]);

  // Load everything when userId is set. Clear first: holding the previous
  // client's rows while the new ones load is how a heading and its numbers
  // end up describing two different people.
  useEffect(() => {
    if (!userId) return;
    setAccounts([]);
    setGoals([]);
    setTransactions([]);
    setMonthlyIncome(0);
    setSavingsRate(0);
    refreshAccounts();
    refreshGoals();
    refreshSnapshot();
    refreshTransactions();
  }, [userId, refreshAccounts, refreshGoals, refreshSnapshot, refreshTransactions]);

  const totalAssets = accounts
    .filter(a => a.type === "asset")
    .reduce((s, a) => s + a.balance, 0);

  const totalLiabilities = accounts
    .filter(a => a.type === "liability")
    .reduce((s, a) => s + a.balance, 0);

  const netWorth = totalAssets - totalLiabilities;

  const totalMonthlySavings = goals.reduce((s, g) => s + (g.monthly || 0), 0);

  // Memoized because this value reaches the whole app. As a fresh object
  // literal it was a new identity on every provider render, so each of the
  // four initial responses re-rendered every consumer in the tree — and no
  // amount of React.memo downstream could help, since the context value
  // itself had changed. Now consumers re-render when the DATA changes.
  const value = useMemo(() => ({
    accounts, totalAssets, totalLiabilities, netWorth,
    goals, totalMonthlySavings,
    monthlyIncome, savingsRate,
    transactions, loadingTransactions,
    loadingAccounts, loadingGoals,
    refreshAccounts, refreshGoals, refreshSnapshot, refreshTransactions,
    userId, setUserId,
  }), [
    accounts, totalAssets, totalLiabilities, netWorth,
    goals, totalMonthlySavings,
    monthlyIncome, savingsRate,
    transactions, loadingTransactions,
    loadingAccounts, loadingGoals,
    refreshAccounts, refreshGoals, refreshSnapshot, refreshTransactions,
    userId,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppData() {
  return useContext(AppContext);
}
